import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { cashReconciliationBXService } from '../../../../../BoostXpressAdmin/Services/cashReconciliationBXService'
import {
  CashReconciliationSummary,
  FinanceLateReport,
  ReportStatus,
} from '../../../../../BoostXpressAdmin/Model/cashReconciliationXModel'
import { formatMinorCurrency } from '../../../../../BoostXpressAdmin/Utils/cashReconciliationMoney'
import FinanceTable from './FinanceTable'
import TimerReset from './Icons/TimerReset'
import { FinanceColumn, FinanceRow } from '../Finance'

const formatReportDate = (value: string) =>
  new Intl.DateTimeFormat('en-NG', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`))

const formatOverdueDuration = (totalSeconds: number) => {
  const hours = Math.floor(Math.max(0, totalSeconds) / 3600)
  const days = Math.floor(hours / 24)
  const remainingHours = hours % 24

  if (days && remainingHours) {
    return `${days} day${days === 1 ? '' : 's'}, ${remainingHours} hour${remainingHours === 1 ? '' : 's'}`
  }
  if (days) return `${days} day${days === 1 ? '' : 's'}`
  if (hours) return `${hours} hour${hours === 1 ? '' : 's'}`
  return 'less than an hour'
}

interface CashReconciliationTableProps {
  pageNumber: number
  pageSize: number
  searchTerm: string
  status?: ReportStatus | 'ALL'
  onTotalChange: (total: number, pageSize?: number) => void
  onLoadingChange?: (loading: boolean) => void
}

const CashReconciliationTable: React.FC<CashReconciliationTableProps> = ({
  pageNumber,
  pageSize,
  searchTerm,
  status,
  onTotalChange,
  onLoadingChange,
}) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<CashReconciliationSummary[]>([])
  const [error, setError] = useState('')
  const [retryCount, setRetryCount] = useState(0)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)
  const [oldestLateReport, setOldestLateReport] = useState<FinanceLateReport | null>(null)
  const [lateReportCount, setLateReportCount] = useState(0)

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedSearchTerm(searchTerm), 300)
    return () => window.clearTimeout(timeoutId)
  }, [searchTerm])

  useEffect(() => {
    let cancelled = false

    const loadData = async () => {
      setLoading(true)
      onLoadingChange?.(true)
      setError('')
      try {
        const normalizedSearch = debouncedSearchTerm.trim().toLocaleLowerCase()

        if (normalizedSearch || status === 'ALL') {
          const summaries =
            status === 'ALL'
              ? await cashReconciliationBXService.listAllFinanceStatuses()
              : await cashReconciliationBXService.listAll({ status })
          if (cancelled) return

          const filteredSummaries = normalizedSearch
            ? summaries.filter((summary) =>
                [summary.stationName, summary.stationId, summary.submittedByName].some((value) =>
                  value?.toLocaleLowerCase().includes(normalizedSearch),
                ),
              )
            : summaries
          const pageStart = (pageNumber - 1) * pageSize
          setData(filteredSummaries.slice(pageStart, pageStart + pageSize))
          onTotalChange(filteredSummaries.length)
        } else {
          const response = await cashReconciliationBXService.list({
            page: pageNumber,
            limit: pageSize,
            status,
          })
          if (cancelled) return

          const summaries = response.data.data ?? []
          setData(summaries)
          onTotalChange(response.data.meta?.total ?? summaries.length)
        }
      } catch {
        if (!cancelled) {
          setData([])
          setError('Station reports could not be loaded. Please try again.')
          onTotalChange(0)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
          onLoadingChange?.(false)
        }
      }
    }

    loadData()

    return () => {
      cancelled = true
    }
  }, [
    debouncedSearchTerm,
    pageNumber,
    pageSize,
    retryCount,
    status,
    onTotalChange,
    onLoadingChange,
  ])

  useEffect(() => {
    let cancelled = false

    const loadLateReports = async () => {
      try {
        const response = await cashReconciliationBXService.listLate()
        if (cancelled) return

        const reports = response.data.data ?? []
        setOldestLateReport(reports[0] ?? null)
        setLateReportCount(response.data.meta?.total ?? reports.length)
      } catch {
        if (!cancelled) {
          setOldestLateReport(null)
          setLateReportCount(0)
        }
      }
    }

    loadLateReports()
    window.addEventListener('focus', loadLateReports)

    return () => {
      cancelled = true
      window.removeEventListener('focus', loadLateReports)
    }
  }, [])

  const emptyMessage = useMemo(() => {
    if (searchTerm.trim()) return 'No station or manager matches your search.'
    if (status === 'ALL') return 'No station reports were found.'
    if (status === 'PUBLISHED') return 'No published station reports were found.'
    if (status === 'FINANCE_CHANGES_REQUESTED') {
      return 'No station reports have been sent back for correction.'
    }
    return 'No station reports are awaiting finance review.'
  }, [searchTerm, status])

  const columns: FinanceColumn[] = [
    { key: 'sn', label: 'S/N' },
    { key: 'station', label: 'Station' },
    { key: 'manager', label: 'Manager' },
    { key: 'reportDate', label: 'Report Date' },
    { key: 'totalSales', label: 'Total Sales' },
    { key: 'status', label: 'Status' },
    { key: 'actions', label: 'Actions' },
  ]

  const rows: FinanceRow[] = data.map((item, index) => {
    let uiStatus = 'pending'
    if (item.status === 'PUBLISHED') uiStatus = 'approved'
    else if (item.status === 'FINANCE_CHANGES_REQUESTED') uiStatus = 'rejected'

    return {
      id: item.resourceId,
      index: (pageNumber - 1) * pageSize + index + 1,
      station: item.stationName || item.stationId,
      manager: item.submittedByName || '-',
      reportDate: item.reportDate,
      totalSales: formatMinorCurrency(item.todaysSalesMinor),
      status: item.status,
      statusLabel: item.status.replace(/_/g, ' '),
      uiStatus,
    }
  })

  const renderCell = (row: FinanceRow, column: FinanceColumn) => {
    switch (column.key) {
      case 'sn':
        return <span className='bx-table__text'>{row.index}</span>
      case 'station':
        return <span className='bx-table__text fw-medium'>{row.station}</span>
      case 'manager':
        return <span className='bx-table__text text-muted'>{row.manager}</span>
      case 'reportDate':
        return <span className='bx-table__text'>{row.reportDate}</span>
      case 'totalSales':
        return <span className='bx-table__text fw-medium'>{row.totalSales}</span>
      case 'status':
        return (
          <span
            className={`bx-status-pill bx-status-pill--${
              row.uiStatus === 'approved'
                ? 'success'
                : row.uiStatus === 'rejected'
                  ? 'failed'
                  : 'pending'
            }`}
          >
            {row.statusLabel}
          </span>
        )
      case 'actions':
        return (
          <button
            type='button'
            className='bx-view-link btn btn-default btn-sm'
            onClick={() => navigate(`/finance/cash-reconciliation/${row.id}`)}
          >
            {row.uiStatus === 'pending' ? 'Review' : 'View'}
          </button>
        )
      default:
        return null
    }
  }

  return (
    <div>
      {oldestLateReport && lateReportCount > 0 ? (
        <div className='bx-finance__alert-banner bx-finance__alert-banner--late' role='status'>
          <div className='bx-finance__alert-icon-wrap'>
            <TimerReset className='bx-finance__alert-icon' size={24} />
          </div>
          <div className='bx-finance__alert-content'>
            <h3 className='bx-finance__alert-title'>
              {lateReportCount === 1
                ? '1 station report is overdue'
                : `${lateReportCount} station reports are overdue`}
            </h3>
            <p className='bx-finance__alert-desc'>
              The oldest is {oldestLateReport.stationName || oldestLateReport.stationId} for{' '}
              {formatReportDate(oldestLateReport.reportDate)}, overdue by{' '}
              {formatOverdueDuration(oldestLateReport.overdueSeconds)}.
            </p>
          </div>
          {oldestLateReport.reportResourceId ? (
            <button
              type='button'
              className='bx-finance__alert-action'
              onClick={() =>
                navigate(`/finance/cash-reconciliation/${oldestLateReport.reportResourceId}`)
              }
            >
              Review report
            </button>
          ) : null}
        </div>
      ) : null}
      <FinanceTable
        columns={columns}
        rows={rows}
        isLoading={loading}
        renderCell={renderCell}
        error={error}
        emptyMessage={emptyMessage}
        onRetry={() => setRetryCount((current) => current + 1)}
      />
    </div>
  )
}

export default CashReconciliationTable
